// iter_129 (2026-06-13) — Print Health Widget.
//
// Compact status icon (always visible in the sidebar) + click-through
// Dialog panel showing the venue's print health: overall status,
// per-station rows, quick actions (Refresh, Open Print Jobs, Retry
// Failed, Test Printer per station).
//
// Data sources are read-only:
//   - GET /api/print/health        — backend aggregation over print_jobs
//   - printBridge.status()         — local WebUSB pairing/connected check
// Mutations are limited to:
//   - POST /api/print/health/retry-failed (operator-driven)
//   - printBridge.testPrintStation(name) (direct ESC/POS test slip)
//
// Auto-refreshes every 15s while the panel is mounted. Manual refresh
// button available. Alert banner displays when failed_count > 0 OR
// any pending row is older than 5 minutes.
//
// Strictly additive: no payment, inventory, order, KDS, or printing
// workflow is touched. Rollback = delete this file and remove the
// mount in Layout.jsx.
import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Printer, ArrowsClockwise, ListChecks, ArrowClockwise, FlowArrow, Warning, CheckCircle, XCircle } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { printBridge } from "@/lib/printBridge";

const REFRESH_MS = 15000;

// Tailwind helpers per status — exact strings so contract tests can
// assert classes without regex gymnastics.
const STATUS_CLASS = {
  healthy:  "text-emerald-700 bg-emerald-100 border-emerald-300",
  warning:  "text-amber-800 bg-amber-100 border-amber-300",
  critical: "text-red-800 bg-red-100 border-red-300",
  unknown:  "text-stone-700 bg-stone-100 border-stone-300",
};
const STATUS_DOT = {
  healthy:  "bg-emerald-500",
  warning:  "bg-amber-500",
  critical: "bg-red-500",
  unknown:  "bg-stone-400",
};

function _fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    const sec = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 1000));
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  } catch (_e) { return "—"; }
}

function _fmtAge(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}

export function PrintHealthWidget({ venueId, compact = false }) {
  const [open, setOpen] = useState(false);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [testingStation, setTestingStation] = useState(null);
  const [bridgeStatus, setBridgeStatus] = useState(() => printBridge.status());
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (venueId) params.venue_id = venueId;
      const r = await api.get("/print/health", { params });
      setHealth(r?.data || null);
    } catch (_e) {
      // Silent — next cycle retries. Surfaces as "unknown" in the
      // widget so operators can still see the icon.
    } finally {
      setLoading(false);
      try { setBridgeStatus(printBridge.status()); } catch (_e) { /* ignore */ }
    }
  }, [venueId]);

  // Initial fetch + auto-refresh while mounted.
  useEffect(() => {
    refresh();
    timerRef.current = setInterval(refresh, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [refresh]);

  // Combined status: backend overall ∪ bridge_connected.
  // Bridge disconnected on this device is a CRITICAL signal per spec.
  const overall = health?.overall;
  let effectiveStatus = overall?.status || "unknown";
  // Bridge disconnected only escalates when WebUSB is supported on
  // this device (otherwise non-printer terminals would always show
  // red). Critical still wins over warning.
  if (bridgeStatus?.supported && !bridgeStatus?.paired) {
    if (effectiveStatus !== "critical") effectiveStatus = "critical";
  }

  const failedCount = overall?.failed_count || 0;
  const pendingCount = overall?.pending_count || 0;
  const oldestPendingAge = overall?.oldest_pending_age_seconds;
  const alertBanner = (failedCount > 0)
    || (oldestPendingAge != null && oldestPendingAge > 5 * 60);

  const onRetryFailed = useCallback(async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      const params = {};
      if (venueId) params.venue_id = venueId;
      const r = await api.post("/print/health/retry-failed", null, { params });
      const n = r?.data?.retried ?? 0;
      if (n > 0) toast.success(`Retrying ${n} failed job${n === 1 ? "" : "s"}.`);
      else toast.info("No failed jobs to retry.");
      await refresh();
    } catch (_e) {
      toast.error("Retry failed. Check your connection and try again.");
    } finally {
      setRetrying(false);
    }
  }, [retrying, venueId, refresh]);

  const onTestPrinter = useCallback(async (stationName) => {
    if (testingStation) return;
    setTestingStation(stationName);
    try {
      const r = await printBridge.testPrintStation(stationName);
      if (r?.ok) toast.success(`Test print sent to ${stationName}.`);
      else if (r?.reason === "no_webusb") toast.error("WebUSB not supported in this browser.");
      else if (r?.reason === "no_device_selected") toast.error("No printer paired. Connect a printer in Settings → Printing.");
      else toast.error(`Test print failed (${r?.reason || "error"}).`);
    } catch (_e) {
      toast.error("Test print failed.");
    } finally {
      setTestingStation(null);
    }
  }, [testingStation]);

  // Icon button — always visible. Tooltip surfaces status without
  // needing to open the dialog.
  const tooltip = (() => {
    if (!health) return "Print health: loading…";
    const parts = [`Print health: ${effectiveStatus}`];
    if (failedCount > 0) parts.push(`${failedCount} failed`);
    if (pendingCount > 0) parts.push(`${pendingCount} pending`);
    if (bridgeStatus?.supported && !bridgeStatus?.paired) parts.push("printer not paired");
    return parts.join(" · ");
  })();

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="print-health-widget"
        data-status={effectiveStatus}
        title={tooltip}
        aria-label={tooltip}
        className={`relative inline-flex items-center justify-center rounded-md border transition-colors ${
          compact ? "w-9 h-9" : "w-full gap-2 px-3 py-2 text-[13px]"
        } ${STATUS_CLASS[effectiveStatus] || STATUS_CLASS.unknown}`}
      >
        <Printer size={16} weight="fill" />
        {!compact && (
          <span className="flex-1 text-left">Print health</span>
        )}
        <span
          data-testid="print-health-dot"
          data-status={effectiveStatus}
          className={`w-2 h-2 rounded-full ${STATUS_DOT[effectiveStatus] || STATUS_DOT.unknown} ${compact ? "absolute top-1 right-1" : ""}`}
        />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="print-health-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer size={18} />
              Print Health
            </DialogTitle>
            <DialogDescription>
              Live status of your printers and print queue. Refreshes every 15 seconds.
            </DialogDescription>
          </DialogHeader>

          {alertBanner && (
            <div
              data-testid="print-health-alert-banner"
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-start gap-2"
            >
              <Warning size={16} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Printer attention required</div>
                <div className="text-xs text-amber-800/90">
                  {pendingCount > 0 && <>{pendingCount} job{pendingCount === 1 ? "" : "s"} waiting. </>}
                  {failedCount > 0 && <>{failedCount} failed job{failedCount === 1 ? "" : "s"}. </>}
                  {overall?.last_successful_print_at && (
                    <>Last successful print {_fmtRelative(overall.last_successful_print_at)}.</>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Overall summary */}
          <div className="rounded-md border border-[rgba(28,25,23,0.08)] p-3 space-y-2" data-testid="print-health-overall">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-[var(--ink-500)]">Overall</div>
              <span
                data-testid="print-health-overall-status"
                data-status={effectiveStatus}
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_CLASS[effectiveStatus] || STATUS_CLASS.unknown}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[effectiveStatus] || STATUS_DOT.unknown}`} />
                {effectiveStatus.toUpperCase()}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              <div>
                <div className="text-[var(--ink-500)]">Last print</div>
                <div data-testid="overall-last-print" className="tabular">{_fmtRelative(overall?.last_successful_print_at)}</div>
              </div>
              <div>
                <div className="text-[var(--ink-500)]">Pending</div>
                <div data-testid="overall-pending-count" className="tabular">{pendingCount}</div>
              </div>
              <div>
                <div className="text-[var(--ink-500)]">Failed</div>
                <div data-testid="overall-failed-count" className={`tabular ${failedCount > 0 ? "text-red-700 font-semibold" : ""}`}>{failedCount}</div>
              </div>
              <div>
                <div className="text-[var(--ink-500)]">Worker</div>
                <div data-testid="overall-worker-status" className="tabular capitalize">{overall?.worker_status || "—"}</div>
              </div>
              <div>
                <div className="text-[var(--ink-500)]">Heartbeat</div>
                <div data-testid="overall-worker-last-seen" className="tabular">{_fmtRelative(overall?.worker_last_seen_at)}</div>
              </div>
              <div>
                <div className="text-[var(--ink-500)]">Printer</div>
                <div data-testid="overall-bridge-status" className="tabular">
                  {!bridgeStatus?.supported && "no WebUSB"}
                  {bridgeStatus?.supported && bridgeStatus?.paired && (
                    <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> connected</span>
                  )}
                  {bridgeStatus?.supported && !bridgeStatus?.paired && (
                    <span className="text-red-700 inline-flex items-center gap-1"><XCircle size={12} weight="fill" /> not paired</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex flex-wrap gap-2" data-testid="print-health-actions">
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              data-testid="print-health-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-[rgba(28,25,23,0.12)] hover:bg-[var(--sand-100)] disabled:opacity-50"
            >
              <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} />
              Refresh Status
            </button>
            <Link
              to="/settings#settings-print-queue"
              data-testid="print-health-open-jobs"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-[rgba(28,25,23,0.12)] hover:bg-[var(--sand-100)]"
            >
              <ListChecks size={13} />
              Open Print Jobs
            </Link>
            <button
              type="button"
              onClick={onRetryFailed}
              disabled={retrying || failedCount === 0}
              data-testid="print-health-retry-failed"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-red-300 text-red-800 hover:bg-red-50 disabled:opacity-50"
            >
              <ArrowClockwise size={13} className={retrying ? "animate-spin" : ""} />
              Retry Failed Jobs{failedCount > 0 ? ` (${failedCount})` : ""}
            </button>
          </div>

          {/* Per-station table */}
          <div className="rounded-md border border-[rgba(28,25,23,0.08)] divide-y divide-[rgba(28,25,23,0.06)]" data-testid="print-health-stations">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-wider text-[var(--ink-500)] bg-[var(--sand-50)]">
              <div className="col-span-3">Station</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-3">Last Print</div>
              <div className="col-span-1 tabular">Pend</div>
              <div className="col-span-1 tabular">Fail</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {(health?.stations || []).map((s) => (
              <div
                key={s.name}
                data-testid={`print-health-station-${s.name.toLowerCase()}`}
                data-status={s.status}
                className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-sm"
              >
                <div className="col-span-3 font-medium flex items-center gap-1.5">
                  <FlowArrow size={12} className="text-[var(--ink-500)]" />
                  <div className="flex flex-col">
                    <span>{s.name}</span>
                    {(s.printer_label || s.printer_id) && (
                      <span
                        data-testid={`station-${s.name.toLowerCase()}-printer`}
                        className="text-[10px] text-[var(--ink-500)] tabular"
                      >
                        → {s.printer_label || s.printer_id}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <span
                    data-testid={`station-${s.name.toLowerCase()}-status`}
                    data-status={s.status}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_CLASS[s.status] || STATUS_CLASS.unknown}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[s.status] || STATUS_DOT.unknown}`} />
                    {s.status}
                  </span>
                </div>
                <div className="col-span-3 text-xs text-[var(--ink-700)] tabular">
                  {_fmtRelative(s.last_successful_print_at)}
                  {s.oldest_pending_age_seconds != null && s.pending_count > 0 && (
                    <div className="text-[10px] text-amber-700">Oldest pend: {_fmtAge(s.oldest_pending_age_seconds)}</div>
                  )}
                </div>
                <div className="col-span-1 tabular text-sm">{s.pending_count}</div>
                <div className={`col-span-1 tabular text-sm ${s.failed_count > 0 ? "text-red-700 font-semibold" : ""}`}>{s.failed_count}</div>
                <div className="col-span-2 text-right">
                  <button
                    type="button"
                    onClick={() => onTestPrinter(s.name)}
                    disabled={testingStation === s.name}
                    data-testid={`station-${s.name.toLowerCase()}-test`}
                    className="text-[11px] px-2 py-1 rounded border border-[rgba(28,25,23,0.12)] hover:bg-[var(--sand-100)] disabled:opacity-50"
                  >
                    {testingStation === s.name ? "Sending…" : "Test Printer"}
                  </button>
                </div>
              </div>
            ))}
            {(!health || (health.stations || []).length === 0) && (
              <div className="px-3 py-4 text-xs text-[var(--ink-500)]" data-testid="print-health-stations-empty">
                No station data yet.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PrintHealthWidget;
